import type { ThinkingMessageDisplay, ToolCallDisplay } from '@/types'
import { WORK_SUMMARY_MAX_KINDS } from '@/config/constants'
import {
  getToolAction,
  TOOL_ACTIONS,
  type ToolAction,
} from '@/utils/toolConfig'

import type { WorkBlockDisplay, WorkStep } from '../groupEvents'

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60

/** The leading **bold** heading of a thought, else its first sentence. */
export function thoughtFirstLine(content: string): string {
  const text = content.trim()
  const heading = /^\*\*(.+?)\*\*/.exec(text)?.[1]
  const line = heading ?? text.split(/(?<=[.!?])\s|\n/)[0] ?? ''
  return line
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*`]/g, '')
    .replace(/^[#>\s]+/, '')
    .trim()
}

function actionPhrase(action: ToolAction, count: number): string {
  const { verb, noun, nounPlural } = TOOL_ACTIONS[action]
  return `${verb} ${count} ${count === 1 ? noun : nounPlural}`
}

/** e.g. "Ran 2 queries, read 1 record and loaded 1 capability". */
export function groupSummary(toolCalls: ToolCallDisplay[]): string {
  const counts = new Map<ToolAction, number>()
  for (const call of toolCalls) {
    const action = getToolAction(call.toolName)
    counts.set(action, (counts.get(action) ?? 0) + 1)
  }
  const kinds = [...counts]
  let parts = kinds.map(([action, count]) => actionPhrase(action, count))
  if (kinds.length > WORK_SUMMARY_MAX_KINDS) {
    const named = WORK_SUMMARY_MAX_KINDS - 1
    const rest = kinds.slice(named).reduce((sum, [, count]) => sum + count, 0)
    parts = [
      ...parts.slice(0, named),
      `${rest} other ${rest === 1 ? 'action' : 'actions'}`,
    ]
  }
  const text =
    parts.length < 2
      ? (parts[0] ?? '')
      : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function formatElapsed(seconds: number): string {
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE)
  return minutes > 0
    ? `${minutes}m ${seconds % SECONDS_PER_MINUTE}s`
    : `${seconds}s`
}

/**
 * "Worked for 42s", or just "Worked" when there is no reliable span: a block
 * with no event after it, persisted calls that share one timestamp, or the
 * hydrate fallback stamping missing timestamps with the load time.
 */
export function workedLabel({ startedAt, endedAt }: WorkBlockDisplay): string {
  const seconds = endedAt
    ? Math.floor((endedAt.getTime() - startedAt.getTime()) / MS_PER_SECOND)
    : 0
  return seconds >= 1 ? `Worked for ${formatElapsed(seconds)}` : 'Worked'
}

export function elapsedLabel(startedAt: Date, nowSeconds: number): string {
  const startSeconds = Math.floor(startedAt.getTime() / MS_PER_SECOND)
  return formatElapsed(Math.max(0, nowSeconds - startSeconds))
}

export function stepToolCalls(steps: WorkStep[]): ToolCallDisplay[] {
  return steps.flatMap((step) =>
    step.type === 'tool_call_group'
      ? step.toolCalls
      : step.type === 'tool_call'
        ? [step]
        : []
  )
}

export function countFailed(toolCalls: ToolCallDisplay[]): number {
  return toolCalls.filter((call) => call.status === 'error').length
}

export function latestThought(
  steps: WorkStep[]
): ThinkingMessageDisplay | undefined {
  return steps.findLast(
    (step): step is ThinkingMessageDisplay =>
      step.type === 'thinking_message' && step.content.trim() !== ''
  )
}

// status flips to 'completed' at TOOL_CALL_END, before the tool runs; the
// result is what marks it finished.
export function isInFlight(call: ToolCallDisplay): boolean {
  return call.result === undefined
}
